import React from 'react';
import 'twin.macro';
import { CahierJournalView } from 'containers/CahierJournal/types';
import { UserLocale } from '@teetsh/app/src/types/user';
import { Classroom } from '@teetsh/app/src/types/classroom';
import { useTranslation } from 'react-i18next';
import { SidebarWithSections } from 'components/SubSidebar/SidebarWithSections';
import { Field, useFormState } from 'react-final-form';
import Button from '@teetsh/app/src/components/Buttons/Button';
import {
  ArrowDownCircleIcon,
  PrinterIcon,
  QueueListIcon,
  SwatchIcon,
} from '@heroicons/react/24/outline';
import { SwitchToggle } from '@teetsh/app/src/components/Form/SwitchToggle';
import FormGroup from '@teetsh/app/src/components/Form/FormGroup';
import { useCJConfigurationDisplay } from '@teetsh/app/src/containers/CahierJournal/useCJConfigurationDisplay';
import { format } from '@teetsh/app/src/utils/time/format';
import { FieldsToDisplay } from 'containers/CahierJournal/Toolbar/Filters/FieldsToDisplay';
import { Checkboxes } from 'components/Form/Checkboxes';
import { Checkbox } from 'components/Form/Checkboxes/Checkbox';
import Select from 'components/Form/Select2/formWrapper';
import { FontsCoupleSelector } from 'components/Form/FontsCoupleSelector';
import { ButtonDisplay } from 'components/Buttons/ButtonDisplay';
import { ColorSelector } from 'components/Form/ColorSelector';
import ButtonGroup from 'components/Form/ButtonGroup';
import { useDownloadSexyCJ } from 'containers/CahierJournal/queries/useDownloadSexyCJ';
import { FontSizeSelector } from 'components/FontSizeSelector';
import { CJPDFSettings } from 'types/cj';
import { MarginsFieldWrapper } from 'components/Margins';
import { BgImageSelectorWithImages } from '@teetsh/app/src/components/Form/BgImageSelector/BgImageSelectorWithImages';

interface Props {
  view: CahierJournalView;
  date: Date;
  classrooms: Classroom[];
  lng: UserLocale;
  tz: string;
  limitedBgSet: boolean;
}

export function Sidebar(props: Props) {
  const { view, date, lng, tz, classrooms, limitedBgSet } = props;
  const { values } = useFormState<CJPDFSettings>();
  const { classroomsId, setClassroomsId, hideEvents, setHideEvents } =
    useCJConfigurationDisplay();
  const { download: downloadCJ, isDownloading } = useDownloadSexyCJ();
  const onDownload = () =>
    downloadCJ({
      view,
      day: format(date, 'fr-FR'),
      direction: values.direction || 'below',
      settings: values,
      lng,
      tz,
    });
  const { t } = useTranslation(['common', 'userSettings', 'cahierJournal']);

  return (
    <SidebarWithSections
      collapsedComponent={
        <SidebarWithSections.CollapsedButton
          icon={ArrowDownCircleIcon}
          title={t('common:buttons.downloadPdf')}
          loading={isDownloading}
          onClick={() => onDownload()}
        />
      }
    >
      <div tw="p-4">
        <Button
          fill
          large
          intent="primary"
          icon={ArrowDownCircleIcon}
          loading={isDownloading}
          onClick={() => onDownload()}
        >
          {t('common:buttons.downloadPdf')}
        </Button>
      </div>

      <SidebarWithSections.Section
        icon={QueueListIcon}
        title={t('common:pdf.elementsAndDisposition')}
      >
        {classrooms.length > 1 && (
          <FormGroup label={t('cahierJournal:toolbar.filter.classrooms.label')}>
            <Checkboxes
              condensed
              value={classroomsId}
              onChange={setClassroomsId}
              options={classrooms.map(cl => ({
                label: cl.name,
                value: cl.id,
              }))}
            />
          </FormGroup>
        )}
        <FormGroup
          label={t('cahierJournal:print.sidebar.seanceElementsToDisplay')}
        >
          <FieldsToDisplay withStatus={false} />
        </FormGroup>
        <FormGroup label={t('cahierJournal:print.sidebar.cjElementsToDisplay')}>
          <div tw="flex flex-wrap gap-2">
            <Field
              name="displaybilan"
              render={({ input }) => (
                <ButtonDisplay
                  active={input.value}
                  onClick={() => input.onChange(!input.value)}
                >
                  {t('cahierJournal:print.bilan')}
                </ButtonDisplay>
              )}
            />
            <Field
              name="displaymaterialssum"
              render={({ input }) => (
                <ButtonDisplay
                  active={input.value}
                  onClick={() => input.onChange(!input.value)}
                >
                  {t('cahierJournal:print.materials')}
                </ButtonDisplay>
              )}
            />
            <Field
              name="disableClassroomBar"
              render={({ input }) => (
                <ButtonDisplay
                  active={!input.value}
                  onClick={() => input.onChange(!input.value)}
                >
                  {t('cahierJournal:print.classrooms')}
                </ButtonDisplay>
              )}
            />
            <Field
              name="hidePlannerEvents"
              render={() => (
                <ButtonDisplay
                  active={!hideEvents}
                  onClick={() => setHideEvents(!hideEvents)}
                >
                  {t('cahierJournal:print.events')}
                </ButtonDisplay>
              )}
            />
            <Field
              name="displayTeacherName"
              render={({ input }) => (
                <ButtonDisplay
                  active={input.value}
                  onClick={() => input.onChange(!input.value)}
                >
                  {t('cahierJournal:print.teacherName')}
                </ButtonDisplay>
              )}
            />
            <Field
              name="displayElevesGroups"
              render={({ input }) => (
                <ButtonDisplay
                  active={input.value}
                  onClick={() => input.onChange(!input.value)}
                >
                  {t('cahierJournal:print.elevesGroups')}
                </ButtonDisplay>
              )}
            />
          </div>
        </FormGroup>
        {['2days', 'week'].includes(view) && (
          <FormGroup label={t('cahierJournal:print.sidebar.daysPosition')}>
            <Field
              name="direction"
              options={[
                {
                  label: t('cahierJournal:print.sidebar.daysStacked'),
                  value: 'below',
                },
                {
                  label: t('cahierJournal:print.sidebar.daysNextToEachOther'),
                  value: 'next',
                },
              ]}
              initialValue={values.direction || 'below'}
              component={Select}
            />
          </FormGroup>
        )}

        {view === 'day' && (
          <FormGroup label={t('cahierJournal:print.sidebar.disposition')}>
            <Field
              name="breakinsideavoid"
              render={({ input }) => (
                <SwitchToggle
                  size="sm"
                  label={t('cahierJournal:print.sidebar.breakInsideAvoid')}
                  checked={input.value}
                  onChange={input.onChange}
                />
              )}
            />
          </FormGroup>
        )}
      </SidebarWithSections.Section>

      <SidebarWithSections.Section
        icon={SwatchIcon}
        title={t('common:pdf.appearance')}
      >
        <FormGroup label={t('common:pdf.fonts')}>
          <Field
            name="font"
            component={({ input: { onChange, value } }) => (
              <FontsCoupleSelector onChange={onChange} value={value} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.fontSize')}>
          <Field
            name="size"
            label={t('userSettings:pdf.fontSize')}
            render={({ input: { onChange, value } }) => (
              <FontSizeSelector onChange={onChange} value={value} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('cahierJournal:print.sidebar.seanceTitleStyle')}>
          <Field
            name="seanceTitleStyle"
            render={({ input: { onChange, value } }) => (
              <div tw="flex gap-x-4">
                <Button
                  onClick={() => onChange('underlined')}
                  active={!value || value === 'underlined'}
                  tw="flex-1"
                >
                  <span tw="underline decoration-2 decoration-orange-500 font-bold">
                    {t('cahierJournal:print.sidebar.underlinedTitles')}
                  </span>
                </Button>
                <Button
                  active={value === 'colored'}
                  onClick={() => onChange('colored')}
                  tw="flex-1"
                >
                  <span tw="text-orange-500 font-bold">
                    {t('cahierJournal:print.sidebar.coloredTitles')}
                  </span>
                </Button>
              </div>
            )}
          ></Field>
        </FormGroup>
        <FormGroup label={t('cahierJournal:print.sidebar.mainTagColor')}>
          <Field
            name="mainTagColor"
            render={({ input: { value, onChange } }) => (
              <ColorSelector value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgColor')}>
          <Field
            name="bgColor"
            render={({ input: { value, onChange } }) => (
              <ColorSelector light value={value} onChange={onChange} />
            )}
          />
        </FormGroup>
        <FormGroup label={t('common:pdf.bgImages')}>
          <Field
            name="bgColor"
            render={({ input: { value, onChange } }) => (
              <BgImageSelectorWithImages
                value={value}
                onChange={onChange}
                limitedBgSet={limitedBgSet}
              />
            )}
          />
        </FormGroup>
      </SidebarWithSections.Section>

      <SidebarWithSections.Section
        icon={PrinterIcon}
        title={t('common:pdf.layout')}
      >
        {(view === 'day' ||
          view === '2days' ||
          (view === 'week' && values.direction === 'below')) && (
          <FormGroup label={t('common:pdf.format')}>
            <Field
              name="landscape"
              render={({ input }) => (
                <ButtonGroup>
                  <Button
                    active={!input.value}
                    onClick={() => input.onChange(false)}
                  >
                    {t('common:pdf.portrait')}
                  </Button>
                  <Button
                    active={input.value}
                    onClick={() => input.onChange(true)}
                  >
                    {t('common:pdf.landscape')}
                  </Button>
                </ButtonGroup>
              )}
            />
          </FormGroup>
        )}
        <FormGroup label={t('common:pdf.margins')}>
          <MarginsFieldWrapper />
        </FormGroup>
        <Field
          name="withPagination"
          type="checkbox"
          render={({ input }) => (
            <Checkbox
              id="withPagination"
              label={t('common:pdf.withPagination')}
              description={t('common:pdf.onlyVisibleOnPdf')}
              {...input}
            />
          )}
        />
      </SidebarWithSections.Section>
    </SidebarWithSections>
  );
}
